import type { Expression } from '../model/poses'
import { morphWeights as mixMorphWeights, nextMascotShape } from '../model/morph-targets'
import type { MascotShape, MorphWeights } from '../model/morph-targets'
import { mascotMoves } from './move-catalog'
import type { MascotMove } from './move-catalog'
import type { Move, MoveEffects, MotionPose } from './move-types'
import { effectChannels, frame, neutral, noEffects, poseChannels } from './move-types'
import { sampleMove, samplePose } from './sample-move'
import { maxFrameDeltaSeconds, movePoseBlendSeconds, shapeMorphDurationSeconds } from './timing'

export interface MoveOptions {
  intensity?: number
  speed?: number
  direction?: 1 | -1
  loop?: boolean
}

export interface MoveRequest {
  elapsedSeconds?: number
  aimAngle?: number
  id: number
  move: MascotMove
  intensity: number
  speed: number
  direction: 1 | -1
  loop: boolean
}

function bounded(value: number | undefined, min: number, max: number) {
  return value === undefined || !Number.isFinite(value) ? 1 : Math.max(min, Math.min(max, value))
}

export function createMoveRequest(
  id: number,
  move: MascotMove,
  options: MoveOptions = {},
): MoveRequest {
  return {
    id,
    move,
    intensity: bounded(options.intensity, 0.4, 1.25),
    speed: bounded(options.speed, 0.5, 1.75),
    direction: options.direction === -1 ? -1 : 1,
    loop: options.loop === true,
  }
}

function keepsFullTurn(move: MascotMove) {
  return move === 'soft_pirouette' || move === 'generating_spin'
}

const morphHopFrames = [
  frame(0),
  frame(0.18, { stretch: -0.1, yaw: -0.16 }),
  frame(0.48, { y: 0.09, yaw: 0.34, roll: 0.08, stretch: 0.05 }),
  frame(0.78, { y: 0.02, yaw: 0.06, stretch: -0.04 }),
  frame(1),
]

function wrapYaw(yaw: number) {
  return Math.atan2(Math.sin(yaw), Math.cos(yaw))
}

function decayPose(pose: MotionPose, dt: number) {
  pose.yaw = wrapYaw(pose.yaw)
  const decay = Math.exp(-20 * dt)
  let active = false
  for (const channel of poseChannels) {
    pose[channel] *= decay
    if (Math.abs(pose[channel]) < 0.000_01) {
      pose[channel] = 0
    } else {
      active = true
    }
  }
  return active
}

function applyEffects(
  definition: Move,
  progress: number,
  request: MoveRequest,
  compact: boolean,
  effects: MoveEffects,
) {
  const keyframes = definition.effects
  if (!keyframes) {
    return
  }
  let index = 0
  while (index < keyframes.length - 2 && progress > keyframes[index + 1].at) {
    index++
  }
  const a = keyframes[index]
  const b = keyframes[index + 1]
  const t = Math.max(0, Math.min(1, (progress - a.at) / (b.at - a.at)))
  const eased = t * t * (3 - 2 * t)
  for (const key of effectChannels) {
    effects[key] = a[key] + (b[key] - a[key]) * eased
  }
  const strength = compact ? request.intensity * 0.45 : request.intensity
  effects.star *= strength
  effects.rippleOpacity *= strength
  effects.trail *= strength
  if (request.direction === -1) {
    effects.trailPosition = 1 - effects.trailPosition
  }
}

function mixChannel(
  channel: keyof MotionPose,
  from: number,
  target: number,
  weight: number,
  request: MoveRequest,
  compact: boolean,
) {
  let value = target
  if (channel === 'yaw' && keepsFullTurn(request.move)) {
    if (compact) {
      value = Math.sin(value) * 0.22 * request.intensity
    }
  } else if (compact) {
    value *= request.intensity * 0.35
  } else {
    value *= request.intensity
  }
  if (channel === 'x' || channel === 'yaw' || channel === 'roll') {
    value *= request.direction
  }
  return from * (1 - weight) + value * weight
}

export class MascotSpecialMoves {
  readonly pose = { ...neutral }
  readonly effects = { ...noEffects }
  expression: Expression | null = null
  pinned: Expression | null = null
  speaking = false
  generating = false
  morphWeights: MorphWeights = mixMorphWeights('basis', 'basis', 1)
  private fromShape: MascotShape = 'basis'
  private toShape: MascotShape = 'basis'
  private shapeElapsed = 0
  private readonly target = { ...neutral }
  private readonly from = { ...neutral }
  private readonly hop = { ...neutral }
  private request: MoveRequest | null = null
  private elapsed = 0
  active = false

  get shape() {
    return this.toShape
  }

  get shapeMorphing() {
    return this.fromShape !== this.toShape
  }

  setShape(shape: MascotShape) {
    this.fromShape = shape
    this.toShape = shape
    this.shapeElapsed = 0
    this.morphWeights = mixMorphWeights(shape, shape, 1)
    Object.assign(this.hop, neutral)
  }

  cycleShape() {
    this.fromShape = this.toShape
    this.toShape = nextMascotShape(this.toShape)
    this.shapeElapsed = 0
  }

  update(request: MoveRequest | null, delta: number, compact = false) {
    Object.assign(this.effects, noEffects)
    this.expression = null
    const dt = Math.max(0, Math.min(delta, maxFrameDeltaSeconds))
    this.advanceShapeMorph(dt)
    if (request && request !== this.request) {
      this.request = request
      this.elapsed = 0
      Object.assign(this.from, this.pose)
      this.from.yaw = wrapYaw(this.from.yaw)
    }
    this.elapsed = request?.elapsedSeconds ?? this.elapsed + dt
    let progress = request ? (this.elapsed * request.speed) / mascotMoves[request.move].duration : 1
    if (request?.loop && progress >= 1) {
      const cycle = mascotMoves[request.move].duration / request.speed
      this.elapsed = this.elapsed % cycle
      progress = (this.elapsed * request.speed) / mascotMoves[request.move].duration
      this.pose.yaw = wrapYaw(this.pose.yaw)
      Object.assign(this.from, this.pose)
    }
    if (!request || progress >= 1) {
      this.active = decayPose(this.pose, dt)
      this.expression = this.pinned
      return this.finishUpdate()
    }
    this.active = true
    const definition = mascotMoves[request.move]
    if (this.speaking) {
      this.expression = this.pinned
    } else {
      for (const [at, expression] of definition.expressions ?? []) {
        if (progress >= at) {
          this.expression = expression
        }
      }
    }
    applyEffects(definition, progress, request, compact, this.effects)
    sampleMove(request.move, progress, this.target)
    const blend = Math.min(1, this.elapsed / movePoseBlendSeconds)
    const weight = blend * blend * (3 - 2 * blend)
    for (const channel of poseChannels) {
      this.pose[channel] = mixChannel(
        channel,
        this.from[channel],
        this.target[channel],
        weight,
        request,
        compact,
      )
    }
    return this.finishUpdate()
  }

  private finishUpdate() {
    if (this.shapeMorphing) {
      this.expression = 'morphing'
    }
    return this.presentedPose()
  }

  private presentedPose() {
    const pose = { ...this.pose }
    for (const channel of poseChannels) {
      pose[channel] += this.hop[channel]
    }
    return pose
  }

  private advanceShapeMorph(dt: number) {
    if (!this.shapeMorphing) {
      this.morphWeights = mixMorphWeights(this.toShape, this.toShape, 1)
      Object.assign(this.hop, neutral)
      return
    }
    this.shapeElapsed += dt
    const t = Math.min(1, this.shapeElapsed / shapeMorphDurationSeconds)
    this.morphWeights = mixMorphWeights(this.fromShape, this.toShape, t)
    if (t >= 1) {
      this.fromShape = this.toShape
      Object.assign(this.hop, neutral)
      return
    }
    samplePose(morphHopFrames, t, this.hop)
  }
}
